import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { WindowSizeController } from '@adornis/chemistry/controllers/window-size-controller.js';
import { acss } from '@adornis/chemistry/directives/acss.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-link.js';
import { type Renderable } from '@adornis/chemistry/renderable.js';
import { LessonStartedResult, getLessonUsageStatistics } from '@chancenwerk/db/eventlogs/ContentResultEventlog.js';
import { PageLoadEventLog, VendorOSStat } from '@chancenwerk/db/eventlogs/PageLoadEventlog.js';
import { SessionEventLog } from '@chancenwerk/db/eventlogs/SessionEventlog.js';
import '@chancenwerk/design/components/cr-card.js';
import '@chancenwerk/design/components/cr-flex.js';
import '@chancenwerk/design/components/cr-grid.js';
import '@chancenwerk/design/components/fonts.js';
import { html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';

/**
 * @customElement campus-page-administration-statistics
 */
@customElement('campus-page-administration-statistics')
export class CampusPageAdministrationStatistics extends ChemistryLitElement {
  @state() size = new WindowSizeController(this);

  @state() private readonly _averageSessionLengthLastMonth = new RXController(
    this,
    SessionEventLog.getAverageSessionLength()().then(averageDuration => Math.round(averageDuration)),
  );
  @state() private readonly _activeUsersLastMonth = new RXController(this, SessionEventLog.getActiveUsersLastMonth()());
  @state() private readonly _startedLessons = new RXController(
    this,
    getLessonUsageStatistics(10)(LessonStartedResult.allFields),
  );
  @state() private readonly _vendorOSStats = new RXController(
    this,
    PageLoadEventLog.getVendorOSStats(10)(VendorOSStat.allFields),
  );

  override render() {
    return html`
      <cr-flex padding="${this.size.mobile ? 'md' : 'lg'}" space="lg">
        <cr-flex space="lg">
          <cr-h2> Statistiken für den Campus </cr-h2>
          <cr-text> Statische Zahlen zum aktuellen Stand der Platform </cr-text>
          <cr-text>
            Weitere Statistiken gibt es auf
            <cr-link
              href="https://cw-statistik.production.adornis.de/d/a7cc24f3-20d6-42d9-a7d0-049eda4b2961/chancenwerk-dashboard?orgId=1"
            >
              Grafana
            </cr-link>
          </cr-text>
        </cr-flex>
        ${this._stats()}
      </cr-flex>
    `;
  }

  private _stats() {
    return html`
      <cr-flex space="md">
        <cr-grid space="md" auto-fit-columns="350px">
          ${this._card(
            this._activeUsersLastMonth.isLoading ? 'Lädt Daten...' : `${this._activeUsersLastMonth.value ?? 0}`,
            'Wie viele aktive Nutzer:innen haben wir auf dem Campus?',
          )}
          ${this._card(
            this._averageSessionLengthLastMonth.isLoading
              ? 'Lädt Daten...'
              : `~${this._averageSessionLengthLastMonth.value ?? 0} Minuten`,
            'Wie lange verweilen die Nutzer:innen durchschnittlich auf dem Campus?',
          )}
        </cr-grid>
        <cr-flex space="md">
          ${this._detailedCard(
            'Welche Lerneinheiten werden wie oft bearbeitet?',
            (this._startedLessons.value ?? []).map(startedLesson => ({
              Lerneinheit: startedLesson.lessonName ?? '',
              'Wie oft bearbeitet?': startedLesson.lessonCount ?? 0,
            })),
            this._startedLessons.isLoading,
          )}
          ${this._detailedCard(
            'Mit welchen Betriebssystemen/Endgeräten wird der Campus genutzt?',
            (this._vendorOSStats.value ?? []).map(vendorOsStat => ({
              Betriebssystem: vendorOsStat.os ?? 'No OS Information',
              Vendor: vendorOsStat.vendor ?? 'No Vendor Information',
              'Anzahl Nutzer': vendorOsStat.users,
            })),
            false,
          )}
        </cr-flex>
      </cr-flex>
    `;
  }

  private _card(title: string | number, subtitle: string) {
    return html`
      <cr-card ${css({ width: '100%' })}>
        <cr-flex space="md">
          <cr-h3 ${css({ color: this.colors.accent })}> ${title} </cr-h3>
          <cr-text> ${subtitle} </cr-text>
        </cr-flex>
      </cr-card>
    `;
  }

  private _detailedCard(title: string, contents: Array<Record<string, Renderable>>, isLoading: boolean) {
    if (!contents.length) return nothing;
    return html`
      <cr-card ${css({ width: '100%' })}>
        <cr-flex space="md"> <cr-h3> ${title} </cr-h3> </cr-flex>
        ${isLoading
          ? html` <x-flex h100 w100 center crossaxis-center> <cr-campus-loader></cr-campus-loader> </x-flex> `
          : html`
              <cr-grid
                columns=${Object.keys(contents.at(0) ?? {}).length}
                ${acss({
                  margin: `0 -${this.spacing.md}`,
                  '[row]': {
                    display: 'contents',
                    '&:nth-child(2n) > *': { background: this.colors.neutral },
                    '& > *:first-child': { paddingLeft: this.spacing.md },
                    '& > *:last-child': { paddingRight: this.spacing.md },
                  },
                })}
              >
                <div row>
                  ${repeat(
                    Object.keys(contents.at(0) ?? {}),
                    contentKey => html` <x-flex padding="sm"> <cr-h4> ${contentKey} </cr-h4> </x-flex> `,
                  )}
                </div>
                ${repeat(
                  contents,
                  content => html`
                    <div row>
                      ${repeat(
                        Object.values(content),
                        value => html` <x-flex padding="sm"> <cr-text> ${value} </cr-text> </x-flex> `,
                      )}
                    </div>
                  `,
                )}
              </cr-grid>
            `}
      </cr-card>
    `;
  }
}
